import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Tablet,
  Computer,
  PhoneAndroid,
  CheckCircle,
  Launch,
  AttachMoney,
  Speed,
  Storage
} from '@mui/icons-material';
import Navigation from './Navigation';
import { devicesContent } from '../content/pages/devices';

// Icon mapping
const IconMap: Record<string, React.ReactElement> = {
  Tablet: <Tablet sx={{ fontSize: 48 }} />,
  Computer: <Computer sx={{ fontSize: 48 }} />,
  PhoneAndroid: <PhoneAndroid sx={{ fontSize: 48 }} />,
  AttachMoney: <AttachMoney sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
  Speed: <Speed sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
  Storage: <Storage sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
};

const Devices: React.FC = () => {
  const c = devicesContent;

  const getDifficultyColor = (difficulty: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (difficulty) {
      case 'Easy': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        <Navigation />

        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
              {c.hero.subtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Quick Comparison Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {c.comparison.title}
            </Typography>
            <Typography variant="h6" sx={{ color: "text.secondary", maxWidth: 800, mx: 'auto', mb: 6 }}>
              {c.comparison.subtitle}
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 3, mb: 6 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {c.comparison.columns.map((col, i) => (
                    <TableCell key={i} align={i === 0 ? "left" : "center"} sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {c.deviceOptions.map((device, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: 'background.default' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ color: 'primary.main' }}>
                          {IconMap[device.iconName]}
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {device.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {device.priceRange}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {device.setupTime}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={device.setupDifficulty}
                        color={getDifficultyColor(device.setupDifficulty)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={device.availability}
                        color={device.availability === 'Available' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {device.availability === 'Available' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          href={device.docsLink}
                          target="_blank"
                          endIcon={<Launch />}
                        >
                          View Guide
                        </Button>
                      ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {c.details.labels.comingSoon}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* Detailed Device Cards */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {c.details.title}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {c.deviceOptions.map((device, index) => (
              <Grid item xs={12} key={index}>
                <Card sx={{ p: 4, boxShadow: 3 }}>
                  <Grid container spacing={4} alignItems="center">
                    {/* Icon and Title */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ color: 'primary.main', mb: 2 }}>
                          {IconMap[device.iconName]}
                        </Box>
                        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                          {device.name}
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 2 }}>
                          {device.priceRange}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                          <Chip
                            label={device.setupDifficulty}
                            color={getDifficultyColor(device.setupDifficulty)}
                            size="small"
                          />
                          <Chip
                            label={device.availability}
                            color={device.availability === 'Available' ? 'success' : 'default'}
                            size="small"
                          />
                        </Stack>
                      </Box>
                    </Grid>

                    {/* Specs and Details */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        {c.details.labels.bestFor}
                      </Typography>
                      <Typography variant="body1" sx={{ color: "text.secondary" }} paragraph>
                        {device.bestFor}
                      </Typography>

                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        {c.details.labels.specs}
                      </Typography>
                      <Stack spacing={1}>
                        {device.specs.map((spec, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {spec}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Grid>

                    {/* Setup Info and CTA */}
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 3, bgcolor: 'background.paper', textAlign: 'center' }}>
                        <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          {c.details.labels.setupTime}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                          {device.setupTime}
                        </Typography>
                        {device.availability === 'Available' ? (
                          <Button
                            variant="contained"
                            fullWidth
                            href={device.docsLink}
                            target="_blank"
                            endIcon={<Launch />}
                            sx={{ mb: 2 }}
                          >
                            {c.details.labels.setupGuide}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            fullWidth
                            disabled
                            sx={{ mb: 2 }}
                          >
                            {c.details.labels.comingSoon}
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* BYOD vs Complete Kits */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {c.byodVsKits.title}
            </Typography>
            <Typography variant="h6" sx={{ color: "text.secondary", maxWidth: 800, mx: 'auto', mb: 6 }}>
              {c.byodVsKits.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* BYOD Column */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 4, boxShadow: 3, bgcolor: 'background.default' }}>
                <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                  {c.byodVsKits.byod.title}
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 4 }}>
                  {c.byodVsKits.byod.description}
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Advantages
                </Typography>
                <Stack spacing={2} sx={{ mb: 4 }}>
                  {c.byodVsKits.byod.advantages.map((adv, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <CheckCircle sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                      <Typography variant="body1">
                        <strong>{adv.title}</strong> {adv.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Considerations
                </Typography>
                <Stack spacing={1} sx={{ mb: 4 }}>
                  {c.byodVsKits.byod.considerations.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ color: "text.secondary" }}>
                      • {item}
                    </Typography>
                  ))}
                </Stack>

                <Button
                  variant="contained"
                  fullWidth
                  component={Link}
                  to="/beta"
                  sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                >
                  {c.byodVsKits.byod.cta}
                </Button>
              </Card>
            </Grid>

            {/* Complete Kits Column */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 4, boxShadow: 3, bgcolor: 'background.default' }}>
                <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  {c.byodVsKits.kits.title}
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 4 }}>
                  {c.byodVsKits.kits.description}
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Advantages
                </Typography>
                <Stack spacing={2} sx={{ mb: 4 }}>
                  {c.byodVsKits.kits.advantages.map((adv, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <CheckCircle sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                      <Typography variant="body1">
                        <strong>{adv.title}</strong> {adv.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Considerations
                </Typography>
                <Stack spacing={1} sx={{ mb: 4 }}>
                  {c.byodVsKits.kits.considerations.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ color: "text.secondary" }}>
                      • {item}
                    </Typography>
                  ))}
                </Stack>

                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                >
                  {c.byodVsKits.kits.cta}
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Hardware Recommendations */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {c.recommendations.title}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {c.recommendations.items.map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card sx={{ height: '100%', p: 3, textAlign: 'center', boxShadow: 2 }}>
                  {IconMap[item.iconName]}
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 2 }}>
                    {item.device}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {item.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.cta.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 6, opacity: 0.9 }}>
              {c.cta.subtitle}
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap">
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/beta"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'white',
                  borderColor: 'white',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderWidth: 2
                  }
                }}
              >
                {c.cta.buttons.beta}
              </Button>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/support"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }
                }}
              >
                {c.cta.buttons.support}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              © 2025 FamilyCast. All rights reserved.
            </Typography>
            <Typography
              variant="body2"
              component="button"
              onClick={() => window.location.href = '/cookie-preferences'}
              sx={{
                opacity: 0.7,
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                color: 'white',
                textDecoration: 'underline',
                '&:hover': { opacity: 1 }
              }}
            >
              Cookie Preferences
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Devices;